import React, { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import {
  VideoPageWrapper,
  VideoTitle,
  VideoDescription,
  VideoPageBackTo,
} from "./VideoPage.styles";
import { BackIcon } from "../../assets/iconComponents/BackIcon";
import { GetTypographyContent } from "../../components/shared/Typography/Typography";
import {
  ANEUPLOIDY_PAGE_URL,
  CARRIER_PAGE_URL,
  LANDING_PAGE_URL,
} from "../../utils/urlConstants";
import carrierNegativeImage from "../../assets/images/carrierNegative.png";
import lowRiskAneuploidyVideo from "../../assets/images/lowRiskAneuploidyVideo.png";
import highRisk22qVideo from "../../assets/images/highRisk22qVideo.png";
import lowRisk22qVideo from "../../assets/images/lowRisk22qVideo.png";
import fetalAntigenNiptDetected from "../../assets/images/fetalAntigenNiptDetected.png";
import fetalAntigenNiptNotDetected from "../../assets/images/fetalAntigenNiptNotDetected.png";
import aneuploidyMonosomyX from "../../assets/images/aneuploidyMonosomyX.png";
import rhdDetectedVideo from "../../assets/images/rhdDetectedVideo.png";
import rhdNotDetectedVideo from "../../assets/images/rhdNotDetectedVideo.png";
import aneuploidyZygosity from "../../assets/images/aneuploidyZygosity.png";
import carrierFetalRisk from "../../assets/images/carrierFetalRisk.png";
import unityAneuploidyInfoVideo from "../../assets/images/unityAneuploidyInfoVideo.png";
import billingInsuranceVideo from "../../assets/images/billingInsuranceVideo.png";
import fetalRhdVideo from "../../assets/images/fetalRhdVideo.png";
import smn1SnpVideo from "../../assets/images/smn1_snp_plus.png";
import VideoLibraryCard from "./VideoLibraryCard";
import { getUserAuth } from "../../recoil/selectors";
import { TrackAnalyticsEvent } from "../../services/Analytics";

const VideoPage = (): React.ReactElement => {
  const { t } = useTranslation();
  const { deviceType } = useDeviceHook();
  const auth = useRecoilValue(getUserAuth);
  const history = useHistory();
  const location = useLocation<{ from?: string } | undefined>();
  const from = location.state?.from;

  useEffect(() => {
    if (auth?.access_token) {
      TrackAnalyticsEvent(
        "VideoLibraryPageView",
        auth?.access_token,
        auth?.user_id,
      );
    }
  }, []);

  const getFromText = (): string => {
    if (!from) return "";
    if (from.startsWith(LANDING_PAGE_URL)) return t("videoLibrary.back.tests");
    if (from.startsWith(ANEUPLOIDY_PAGE_URL))
      return t("videoLibrary.back.aneuploidyResults");
    if (from.startsWith(CARRIER_PAGE_URL))
      return t("videoLibrary.back.carrierResults");
    return "";
  };

  type Video = {
    name: string;
    url: string;
    image: string;
  };

  const videoList: Video[] = [
    {
      name: "CarrierNegative",
      url: "https://player.vimeo.com/video/1057943467?playsinline=0",
      image: carrierNegativeImage,
    },
    {
      name: "LowRiskAneuploidy",
      url: "https://player.vimeo.com/video/1035771695?playsinline=0",
      image: lowRiskAneuploidyVideo,
    },
    {
      name: "HighRisk22Q",
      url: "https://player.vimeo.com/video/1035771784?playsinline=0",
      image: highRisk22qVideo,
    },
    {
      name: "LowRisk22Q",
      url: "https://player.vimeo.com/video/1035771746?playsinline=0",
      image: lowRisk22qVideo,
    },
    {
      name: "FetalAntigenNIPTDetected",
      url: "https://player.vimeo.com/video/1035771652?playsinline=0",
      image: fetalAntigenNiptDetected,
    },
    {
      name: "FetalAntigenNIPTNotDetected",
      url: "https://player.vimeo.com/video/1035771669?playsinline=0",
      image: fetalAntigenNiptNotDetected,
    },
    {
      name: "AneuploidyMonosomyX",
      url: "https://player.vimeo.com/video/1035771767?playsinline=0",
      image: aneuploidyMonosomyX,
    },
    {
      name: "FetalRhdDetected",
      url: "https://player.vimeo.com/video/1035771802?playsinline=0",
      image: rhdDetectedVideo,
    },
    {
      name: "FetalRhdNotDetected",
      url: "https://player.vimeo.com/video/1035771730?playsinline=0",
      image: rhdNotDetectedVideo,
    },
    {
      name: "AneuploidyZygosity",
      url: "https://player.vimeo.com/video/1035771715?playsinline=0",
      image: aneuploidyZygosity,
    },
    {
      name: "CarrierFetalRisk",
      url: "https://player.vimeo.com/video/960668319?playsinline=0",
      image: carrierFetalRisk,
    },
    {
      name: "AneuploidyInfo",
      url: "https://player.vimeo.com/video/960668298?playsinline=0",
      image: unityAneuploidyInfoVideo,
    },
    {
      name: "BillingAndInsuranceProcess",
      url: "https://player.vimeo.com/video/1035775307?playsinline=0",
      image: billingInsuranceVideo,
    },
    {
      name: "FetalRhd",
      url: "https://player.vimeo.com/video/960668334?playsinline=0",
      image: fetalRhdVideo,
    },
    {
      name: "SMN1SNP",
      url: "https://player.vimeo.com/video/1111694716?playsinline=0",
      image: smn1SnpVideo,
    },
  ];

  return (
    <VideoPageWrapper deviceType={deviceType}>
      <VideoPageBackTo onClick={() => history.push(from || LANDING_PAGE_URL)}>
        <BackIcon />
        {GetTypographyContent({
          content: t("videoLibrary.back.label", { destination: getFromText() }),
          size: "medium",
          textColor: "redMedium",
        })}
      </VideoPageBackTo>
      <VideoTitle>{t("videoLibrary.title")}</VideoTitle>
      <VideoDescription>{t("videoLibrary.description")}</VideoDescription>
      {videoList.map((video) => (
        <div
          key={video.url}
          style={{
            marginLeft: "auto",
            marginRight: "auto",
            marginBottom: "15px",
          }}
          data-testid={`video-library-card-${video.name}`}
        >
          <VideoLibraryCard
            name={video.name}
            url={video.url}
            image={video.image}
          />
        </div>
      ))}
    </VideoPageWrapper>
  );
};

export default VideoPage;
